/**
 * Build the multipart FormData payload for POST /api/v1/transcription/summarize.
 *
 * The AI service has two paths (per MDRS-29 VM investigation):
 *
 *  - **multipart `files=<audio>`** → runs `transcribe_and_summarize_with_diarization`
 *    and returns `segmented_full_transcript` (speaker-stamped JSON). This is the
 *    canonical path for both **recorded** and **uploaded** sessions.
 *
 *  - **form field `full_transcript=<text>`** → runs `summarize_existing_transcript`
 *    only. No diarization is produced. Path retained as a safety fallback for the
 *    edge case where the MediaRecorder didn't surface a blob (e.g. permission
 *    revoked mid-session); preferring it would cause the bug MDRS-29 was filed
 *    to fix.
 *
 * The AI service treats the presence of `full_transcript` as a hard signal to
 * use the text path even if `files` is also attached, so we must drop the text
 * field whenever an audio blob is present.
 */
import type { SpeakerHint } from "./SpeakerChecklist";

type BuildSummarizeFormDataParams = {
  caseNumber: string;
  examineeName: string;
  audioBlob?: Blob | null;
  /** Used only when `audioBlob` is null/undefined. */
  fallbackTranscript?: string;
  /**
   * Consent-modal speaker names/roles for the session that produced
   * `audioBlob` (MDRS-163 follow-on) — forwarded as JSON so the batch
   * diarization pipeline can identify speakers by these real names instead
   * of generic role words. Omitted entirely when empty/absent (e.g. no
   * consent-modal-originated session) rather than sending an empty array.
   */
  speakerHints?: SpeakerHint[];
};

export const buildSummarizeFormData = ({
  caseNumber,
  examineeName,
  audioBlob,
  fallbackTranscript,
  speakerHints,
}: BuildSummarizeFormDataParams): FormData => {
  const formData = new FormData();
  formData.append("case_number", caseNumber);
  formData.append("examinee_name", examineeName);
  if (audioBlob) {
    formData.append("files", audioBlob, "recording.webm");
  } else if (fallbackTranscript) {
    formData.append("full_transcript", fallbackTranscript);
  }
  if (speakerHints && speakerHints.length > 0) {
    formData.append("speaker_hints", JSON.stringify(speakerHints));
  }
  return formData;
};
